import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import { Link2, TrendingUp, ClickTrack, Target, Plus, Loader2 } from "lucide-react";
import {
  HeroCard, StatCard, ChartCard, InsightCard, Toolbar, SkeletonCard, EmptyState, RankingCard
} from "@/components/marketing/common";
import { MARKETING_COLORS } from "@/config/marketingColors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 shadow-lg">
      <p className="text-xs text-slate-400 font-medium">{payload[0]?.payload?.date || payload[0]?.payload?.name || ''}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-bold text-white">
          {p.name}: {typeof p.value === "number" ? p.value.toFixed(0) : p.value}
        </p>
      ))}
    </div>
  );
};

export function LinksTab() {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [links, setLinks] = useState([]);
  const [period, setPeriod] = useState('30');
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", url: "", campaign: "", active: true });

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await authAxiosRef.current.get("/marketing/links");
      setLinks(res.data || []);
    } catch {
      toast.error("Erro ao carregar links");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const create = async () => {
    if (!form.name || !form.url) { toast.error("Nome e URL obrigatórios"); return; }
    try {
      await authAxios.post("/marketing/links", form);
      toast.success("Link criado");
      setShowNew(false);
      setForm({ name: "", url: "", campaign: "", active: true });
      fetchData();
    } catch {
      toast.error("Erro ao criar link");
    }
  };

  const toggle = async (id) => {
    try {
      await authAxios.patch(`/marketing/links/${id}/toggle`);
      fetchData();
    } catch {
      toast.error("Erro ao atualizar");
    }
  };

  if (loading) return <SkeletonCard count={4} type="stat" />;

  if (!links || links.length === 0) {
    return <EmptyState title="Sem links" message="Crie seu primeiro link de rastreamento" />;
  }

  const totalClicks = links.reduce((sum, l) => sum + (l.clicks || 0), 0);
  const totalRevenue = links.reduce((sum, l) => sum + ((l.clicks || 0) * (l.avg_conversion || 0) * 15), 0);
  const activeLinks = links.filter(l => l.active).length;
  const conversionRate = totalClicks > 0 ? (links.reduce((sum, l) => sum + ((l.conversions || 0) / Math.max((l.clicks || 1), 1) * 100), 0) / links.length).toFixed(1) : '0';
  const topLink = links.sort((a, b) => (b.clicks || 0) - (a.clicks || 0))[0];

  const timelineData = [
    { date: 'D1', clicks: 342 },
    { date: 'D2', clicks: 418 },
    { date: 'D3', clicks: 297 },
    { date: 'D4', clicks: 523 },
    { date: 'D5', clicks: 389 },
    { date: 'D6', clicks: 612 },
    { date: 'D7', clicks: 485 },
  ];

  const topLinks = links.slice(0, 5).map(l => ({
    name: l.name,
    value: l.clicks || 0,
    revenue: (l.clicks || 0) * (l.avg_conversion || 0) * 15,
  }));

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <HeroCard
        title="Links Rastreados"
        metric={totalClicks.toString()}
        subMetrics={[
          { label: "Ativos", value: activeLinks.toString() },
          { label: "Conversão", value: `${conversionRate}%`, trend: "up", change: 3 },
          { label: "Receita", value: `€${(totalRevenue / 1000).toFixed(0)}k`, trend: "up", change: 5 },
        ]}
        color={MARKETING_COLORS.Pixels}
      />

      {/* Period Selector */}
      <Toolbar period={period} onPeriodChange={setPeriod} />

      {/* KPI Cards */}
      <div className="grid lg:grid-cols-4 md:grid-cols-2 sm:grid-cols-1 gap-4">
        <StatCard label="Total Cliques" value={totalClicks.toString()} icon={Link2} trend="up" change={14} />
        <StatCard label="Conversão" value={`${conversionRate}%`} icon={TrendingUp} trend="up" change={2} />
        <StatCard label="Links Ativos" value={activeLinks.toString()} icon={Target} trend="neutral" change={0} />
        <StatCard label="Receita Gerada" value={`€${(totalRevenue / 1000).toFixed(0)}k`} icon={TrendingUp} trend="up" change={8} />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        <ChartCard title="Top Links" subtitle="Mais clicados" height="300px">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={topLinks}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="name" tick={{ fill: "#9ca3af", fontSize: 9 }} />
              <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" name="Cliques" fill={MARKETING_COLORS.Pixels} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Evolução" subtitle="Últimos 7 dias" height="300px">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={timelineData}>
              <defs>
                <linearGradient id="gradLinks" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={MARKETING_COLORS.Pixels} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={MARKETING_COLORS.Pixels} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="date" tick={{ fill: "#9ca3af", fontSize: 10 }} />
              <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="clicks" name="Cliques" stroke={MARKETING_COLORS.Pixels} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Insights */}
      <div className="grid md:grid-cols-3 gap-4">
        <InsightCard type="success" title="Link Vencedor" message={topLink ? `${topLink.name} com ${topLink.clicks || 0} cliques` : "Sem dados"} />
        <InsightCard type={conversionRate > 5 ? "success" : "insight"} title="Conversão" message={`Taxa de ${conversionRate}% — ${conversionRate > 5 ? "Excelente!" : "Otimize"}`} />
        <InsightCard type="insight" title="Ação Recomendada" message="Promova links de alta performance em horários de pico" />
      </div>

      {/* Ranking */}
      {topLinks.length > 0 && (
        <RankingCard
          title={`Links Mais Clicados (${topLinks.length})`}
          items={topLinks.map(l => ({ name: l.name, value: l.value, max: topLinks[0].value }))}
          formatValue={(v) => `${v} cliques`}
        />
      )}

      {/* Create Button */}
      <div className="flex justify-end">
        <Dialog open={showNew} onOpenChange={setShowNew}>
          <DialogTrigger asChild>
            <Button size="lg" className="gap-2"><Plus className="w-4 h-4" />Novo Link</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>Novo Link Rastreado</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2"><Label>Nome</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Amigos Felices" /></div>
              <div className="space-y-2"><Label>URL</Label><Input value={form.url} onChange={e => setForm({...form, url: e.target.value})} placeholder="https://exemplo.com" /></div>
              <div className="space-y-2"><Label>Campanha</Label><Input value={form.campaign} onChange={e => setForm({...form, campaign: e.target.value})} placeholder="WhatsApp" /></div>
            </div>
            <DialogFooter><Button onClick={create} className="w-full" size="lg">Criar Link</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Links List */}
      <div className="space-y-2 pt-4 border-t">
        <h3 className="text-sm font-semibold">Gerenciar Links</h3>
        {links.map(l => (
          <div key={l.id} className="flex items-center justify-between p-3 bg-card border border-border rounded-lg">
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm">{l.name}</p>
              <p className="text-xs text-muted-foreground">{l.campaign || "Sem campanha"} • {l.clicks || 0} cliques</p>
            </div>
            <div className="flex items-center gap-2"><Switch checked={l.active} onCheckedChange={() => toggle(l.id)} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}
